import React, { useState, useEffect } from 'react';
import { FileText, Copy, Check, Calendar, School, AlertCircle } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';

const TranscriptRequestGenerator = ({ collegeList = [], studentProfile }) => {
  const { currentUser } = useFamilyContext();
  const [counselorName, setCounselorName] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [copied, setCopied] = useState(false);
  const [includeStudentId, setIncludeStudentId] = useState(true);
  const [studentId, setStudentId] = useState('');

  useEffect(() => {
    // Pre-select all colleges
    if (collegeList.length > 0) {
      setSelectedColleges(collegeList.map((_, idx) => idx));
    }

    // Set default deadline (2 weeks from earliest application deadline or from today)
    const today = new Date();
    const twoWeeksFromNow = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);

    // Try to find earliest deadline from college list
    const earliestDeadline = collegeList.reduce((earliest, college) => {
      const deadline = college.application_deadline || college.deadline;
      if (deadline) {
        const deadlineDate = new Date(deadline);
        const requestBy = new Date(deadlineDate.getTime() - 14 * 24 * 60 * 60 * 1000);
        return !earliest || requestBy < earliest ? requestBy : earliest;
      }
      return earliest;
    }, null);

    const defaultDeadline = earliestDeadline || twoWeeksFromNow;
    setDeadline(defaultDeadline.toISOString().split('T')[0]);
  }, [collegeList]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const getCollegeName = (college) => {
    if (typeof college === 'string') return college;
    // Handle college_journey_colleges structure with global_colleges join
    return college.global_colleges?.name || college.name || college.college_name || 'Unknown College';
  };

  const generateLetter = () => {
    const selectedCollegeNames = selectedColleges
      .map(idx => collegeList[idx])
      .map(college => `    •    ${getCollegeName(college)}`)
      .join('\n');

    const studentName = currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : studentProfile?.name || '[Your Name]';

    const greeting = counselorName
      ? `Hi ${counselorName.includes('Mr.') || counselorName.includes('Ms.') || counselorName.includes('Mrs.') || counselorName.includes('Dr.') ? counselorName : `Mr./Ms. ${counselorName}`},`
      : 'Dear Counselor,';

    const letter = `Subject: Transcript Request

${greeting}

Could you please send my official transcript to the following colleges by ${formatDate(deadline)}?
${selectedCollegeNames}

Also, could you let me know if there are any forms or fees I need to complete as part of the process?

Thank you so much for your help!

${studentName}${includeStudentId && studentId ? `\nStudent ID: ${studentId}` : ''}`;

    return letter;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generateLetter());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleCollege = (idx) => {
    setSelectedColleges(prev => {
      if (prev.includes(idx)) {
        return prev.filter(i => i !== idx);
      }
      return [...prev, idx];
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gradient-to-r from-blue-50 to-violet-50 rounded-2xl p-8 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <FileText className="w-8 h-8 text-blue-600" />
          <h2 className="text-3xl font-bold text-slate-900">
            Transcript Request Letter
          </h2>
        </div>
        <p className="text-slate-700 leading-relaxed">
          Generate a professional transcript request email for your counselor. No AI needed - just fill in the details!
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Configuration Panel */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Counselor Name (optional)
            </label>
            <input
              type="text"
              value={counselorName}
              onChange={(e) => setCounselorName(e.target.value)}
              placeholder="Mr. Jones / Ms. Smith"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              Leave blank for "Dear Counselor"
            </p>
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Request Deadline
            </label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              Usually 2 weeks before application deadline
            </p>
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Student ID (optional)
            </label>
            <input
              type="text"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              placeholder="123456"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={includeStudentId}
                onChange={(e) => setIncludeStudentId(e.target.checked)}
                className="rounded text-blue-600"
              />
              <span className="text-sm text-slate-600">Include Student ID</span>
            </label>
          </div>

          <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-700">
                <School className="w-4 h-4 inline mr-1" />
                Select Colleges ({selectedColleges.length}/{collegeList.length})
              </label>
              <button
                onClick={() => setSelectedColleges(selectedColleges.length === collegeList.length ? [] : collegeList.map((_, idx) => idx))}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {selectedColleges.length === collegeList.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {collegeList.map((college, idx) => (
                <label key={idx} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedColleges.includes(idx)}
                    onChange={() => toggleCollege(idx)}
                    className="rounded text-blue-600"
                  />
                  <span className="text-sm text-slate-700">{getCollegeName(college)}</span>
                </label>
              ))}
              {collegeList.length === 0 && (
                <p className="text-sm text-slate-500 italic">No colleges in your list yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Preview</h3>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                copied
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy to Clipboard
                </>
              )}
            </button>
          </div>

          {selectedColleges.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Select Colleges</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Please select at least one college from your list to include in the transcript request.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap break-words">
              {generateLetter()}
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Tips for Transcript Requests</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Submit requests at least 2 weeks before application deadlines</li>
          <li>• Some schools charge a small fee for transcripts (usually $5-10)</li>
          <li>• Follow up with your counselor if you don't get confirmation within 3-4 days</li>
          <li>• Check if your school uses Naviance, Parchment, or another electronic system</li>
          <li>• Keep a record of when you made each request</li>
        </ul>
      </div>
    </div>
  );
};

export default TranscriptRequestGenerator;